/**
 * Pure data model + parsing for GitHub reference cards, with no React/Primer
 * dependencies. Kept separate from {@link ./GitHubReferenceCards} so the runtime
 * layer (e.g. client-tool definitions) can share the tool name and input parser
 * without pulling the UI component's Primer/octicon/CSS imports into its bundle.
 */

import {isGitHubControlledHost} from '../helpers/github-hosts'

/** The kind of entity a reference row points at. */
export type GithubReferenceType = 'pr' | 'issue' | 'commit' | 'repo' | 'discussion' | 'release'

/** The lifecycle state of a PR/issue reference, when applicable. */
export type GithubReferenceState = 'open' | 'closed' | 'merged' | 'draft'

/** A single GitHub reference to render as a card. */
export interface GithubReference {
  type: GithubReferenceType
  title: string
  /** Canonical https URL on github.com. Validated by {@link safeGithubUrl}. */
  url: string
  number?: number
  state?: GithubReferenceState
  /** `owner/name` slug the reference belongs to, e.g. `github/copilot`. */
  repo?: string
}

/** A GitHub PR or issue whose backend payload may not include a title. */
export interface GitHubResourceReference {
  type: 'pr' | 'issue'
  title?: string
  url: string
  number: number
  state?: GithubReferenceState
  repo?: string
}

/** A branch comparison rendered with Primer branch names. */
export interface BranchReference {
  type: 'branch'
  url: string
  baseBranch: string
  headBranch: string
}

/** A validated non-GitHub resource that opens in a new tab. */
export interface ExternalReference {
  type: 'external'
  title: string
  url: string
  metadata?: string
}

/** A GitHub user profile reference supplied by a trusted caller. */
export interface UserReference {
  type: 'user'
  title: string
  url: string
  metadata?: string
}

/** Data-free rendering model shared by chat references and task artifacts. */
export type ReferenceItem =
  | GithubReference
  | GitHubResourceReference
  | BranchReference
  | ExternalReference
  | UserReference

const GITHUB_REFERENCE_TYPES = new Set<GithubReferenceType>(['pr', 'issue', 'commit', 'repo', 'discussion', 'release'])

const GITHUB_REFERENCE_STATES = new Set<GithubReferenceState>(['open', 'closed', 'merged', 'draft'])

/**
 * Accept only `https://` URLs whose host is a GitHub-controlled domain
 * (github.com, ghe.com proxima tenants, or a subdomain of either — see
 * {@link isGitHubControlledHost}). Reference rows come from untrusted,
 * model-supplied tool input, so this blocks `javascript:`/`data:` URLs and
 * off-GitHub hosts from ever reaching an `<a href>`. Returns the normalized URL
 * string, or `null` when unsafe.
 */
export function safeGithubUrl(url: string): string | null {
  let parsed: URL
  try {
    // References come from arbitrary model-supplied input, not the current page,
    // so we must parse them as absolute URLs without a location-origin base.
    // eslint-disable-next-line no-restricted-syntax -- intentionally parse an arbitrary absolute URL
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:') return null
  if (!isGitHubControlledHost(parsed.hostname)) return null
  return parsed.toString()
}

function safeExternalUrl(url: string): string | null {
  let parsed: URL
  try {
    // Model-supplied external references must be absolute URLs.
    // eslint-disable-next-line no-restricted-syntax -- intentionally parse an arbitrary absolute URL
    parsed = new URL(url)
  } catch {
    return null
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
  if (parsed.username || parsed.password || !parsed.host) return null
  return parsed.toString()
}

/**
 * Coerce one raw, model-supplied reference row into a renderable reference.
 * Unknown GitHub types fall back to `repo`; unknown states are dropped.
 */
export function normalizeReference(raw: unknown): ReferenceItem | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const rawType = typeof r.type === 'string' ? r.type : undefined
  const title = typeof r.title === 'string' ? r.title : undefined
  const rawUrl = typeof r.url === 'string' ? r.url : undefined
  if (!rawUrl) return null

  if (rawType === 'branch') {
    const baseBranch = typeof r.baseBranch === 'string' ? r.baseBranch : undefined
    const headBranch = typeof r.headBranch === 'string' ? r.headBranch : undefined
    const url = safeGithubUrl(rawUrl)
    if (!baseBranch || !headBranch || !url) return null
    return {type: 'branch', url, baseBranch, headBranch}
  }

  if (rawType === 'external') {
    const url = safeExternalUrl(rawUrl)
    if (!title || !url) return null
    return {
      type: 'external',
      title,
      url,
      ...(typeof r.metadata === 'string' ? {metadata: r.metadata} : {}),
    }
  }

  if (rawType === 'user') {
    const url = safeGithubUrl(rawUrl)
    if (!title || !url) return null
    return {
      type: 'user',
      title,
      url,
      ...(typeof r.metadata === 'string' ? {metadata: r.metadata} : {}),
    }
  }

  const url = safeGithubUrl(rawUrl)
  if (!url) return null
  const type =
    rawType && GITHUB_REFERENCE_TYPES.has(rawType as GithubReferenceType) ? (rawType as GithubReferenceType) : 'repo'
  const rawState = typeof r.state === 'string' ? r.state.toLowerCase() : undefined
  const state =
    rawState && GITHUB_REFERENCE_STATES.has(rawState as GithubReferenceState)
      ? (rawState as GithubReferenceState)
      : undefined
  if ((type === 'pr' || type === 'issue') && typeof r.number === 'number') {
    return {
      type,
      url,
      number: r.number,
      ...(state ? {state} : {}),
      ...(typeof r.repo === 'string' ? {repo: r.repo} : {}),
      ...(title ? {title} : {}),
    }
  }
  if (!title) return null
  return {
    type,
    title,
    url,
    ...(typeof r.number === 'number' ? {number: r.number} : {}),
    ...(state ? {state} : {}),
    ...(typeof r.repo === 'string' ? {repo: r.repo} : {}),
  }
}

/** Normalize a raw chat tool `items` array into the shared reference variants. */
export function normalizeGithubReferences(items: unknown): ReferenceItem[] {
  if (!Array.isArray(items)) return []
  const refs: ReferenceItem[] = []
  for (const raw of items) {
    const ref = normalizeReference(raw)
    if (ref) refs.push(ref)
  }
  return refs
}

/**
 * Name of the render-only, client-contributed tool the agent is instructed to
 * call in place of writing a markdown list of GitHub items. Shared so every
 * surface that renders an AHP timeline can special-case it identically.
 */
export const DISPLAY_GITHUB_REFERENCES_TOOL_NAME = 'display_github_references'

/**
 * Parse a `display_github_references` tool call's input (a JSON string or an
 * already-parsed object) into safe, normalized references. Returns `[]` for
 * malformed input so callers can render nothing rather than throw.
 */
export function parseGithubReferencesToolInput(toolInput: unknown): ReferenceItem[] {
  let parsed: unknown = toolInput
  if (typeof toolInput === 'string') {
    try {
      parsed = JSON.parse(toolInput)
    } catch {
      return []
    }
  }
  if (!parsed || typeof parsed !== 'object') return []
  return normalizeGithubReferences((parsed as {items?: unknown}).items)
}
